import { Avatar, Badge, Group, Loader, Paper, Stack, Table, Text, Title } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api.js";

interface InternalUser {
  id: string;
  email: string;
  displayName: string;
  status: string;
  roles: string[];
  lastLoginAt: string | null;
}

const roleColor = (role: string) => {
  if (role.includes("super_admin")) return "red";
  if (role.includes("admin")) return "orange";
  if (role.includes("security")) return "grape";
  if (role.includes("billing")) return "teal";
  return "gray";
};

export const TeamPage = () => {
  const { data, isLoading } = useQuery({
    queryKey: ["internal", "team"],
    queryFn: () => api.get<{ users: InternalUser[] }>("/internal/team")
  });

  const users = data?.users ?? [];

  return (
    <Stack gap="md">
      <Title order={2}>Team</Title>
      <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
        {isLoading ? (
          <Group justify="center" p="xl">
            <Loader size="sm" />
          </Group>
        ) : (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>User</Table.Th>
                <Table.Th>Roles</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Last login</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {users.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Text size="sm" c="dimmed" ta="center" p="md">
                      No internal users found
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                users.map((u) => (
                  <Table.Tr key={u.id}>
                    <Table.Td>
                      <Group gap="sm">
                        <Avatar size="sm" radius="xl" color="brand">
                          {u.displayName[0]?.toUpperCase() ?? "?"}
                        </Avatar>
                        <div>
                          <Text size="sm" fw={500}>
                            {u.displayName}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {u.email}
                          </Text>
                        </div>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        {u.roles.map((r) => (
                          <Badge key={r} size="xs" color={roleColor(r)} variant="light">
                            {r}
                          </Badge>
                        ))}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="xs" color={u.status === "active" ? "teal" : "gray"}>
                        {u.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : "Never"}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        )}
      </Paper>
    </Stack>
  );
};
